import { useCallback, useEffect, useMemo, useState } from "react";
import api, { errorMessage } from "../lib/api";
import LinkedEntityFilters from "./LinkedEntityFilters";
import { GhostButton, inputClass } from "./Primitives";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}
function currentMonthRange() {
  const now = new Date();
  return { start: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), end: isoDate(now) };
}

// Each metric column: key, short header, tooltip.
const COLS = [
  { key: "late_lt15",         label: "Late < 15m",   help: "Clock-ins less than 15 minutes late" },
  { key: "late_15_30",        label: "Late 15-30m",  help: "Clock-ins 15-30 minutes late" },
  { key: "late_gt30",         label: "Late > 30m",   help: "Clock-ins more than 30 minutes late" },
  { key: "early_lt15",        label: "Early < 15m",  help: "Clock-outs less than 15 minutes early" },
  { key: "early_15_30",       label: "Early 15-30m", help: "Clock-outs 15-30 minutes early" },
  { key: "early_gt30",        label: "Early > 30m",  help: "Clock-outs more than 30 minutes early" },
  { key: "missed_clockouts",  label: "Missed CO",    help: "Ended shifts where guard checked in but never checked out" },
  { key: "incidents",         label: "Incidents",    help: "Total incidents filed in range" },
  { key: "maintenance",       label: "MRs",          help: "Maintenance requests filed in range" },
  { key: "geofence_breaches", label: "Geo Breach",   help: "Geofence breaches started in range" },
  { key: "tours_completed",   label: "Tours Done",   help: "Tour visits actually logged (capped per stop-expectation)" },
  { key: "tour_completion_pct", label: "Tour %",     help: "Completed vs expected tour visits × 100", isPct: true },
];

function toneForValue(col, v, row) {
  if (col.key === "tour_completion_pct") {
    // Sites with no tours configured shouldn't scream red just because 0% < 80%.
    if (!row?.tours_expected) return "text-zinc-600";
    if (v >= 95) return "text-[#34C759]";
    if (v >= 80) return "text-[#FF9F0A]";
    return "text-[#FF3B30]";
  }
  if (col.key.startsWith("late_gt30") || col.key.startsWith("early_gt30") || col.key === "missed_clockouts") {
    return v > 0 ? "text-[#FF3B30] font-bold" : "text-zinc-500";
  }
  if (col.key.startsWith("late_") || col.key.startsWith("early_")) {
    return v > 0 ? "text-[#FF9F0A]" : "text-zinc-500";
  }
  return v > 0 ? "text-zinc-200" : "text-zinc-600";
}

export default function SitePerformanceTab() {
  const [{ start, end }, setRange] = useState(currentMonthRange());
  const [entity, setEntity] = useState({ client_names: [], site_ids: [], guard_ids: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("site_name");
  const [sortDir, setSortDir] = useState("asc");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (entity.site_ids?.length) params.site_id = entity.site_ids.join(",");
      if (entity.client_names?.length) params.client_name = entity.client_names.join(",");
      const res = await api.get("/metrics/site-performance", { params });
      setData(res.data);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load site performance"));
      setData(null);
    }
    setLoading(false);
  }, [start, end, entity]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtersShow = useMemo(() => ({ client: true, site: true, guard: false }), []);

  const sortedSites = useMemo(() => {
    if (!data?.sites) return [];
    const rows = [...data.sites];
    rows.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (typeof av === "string") { av = (av || "").toLowerCase(); bv = (bv || "").toLowerCase(); }
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "site_name" ? "asc" : "desc");
    }
  };

  return (
    <div className="space-y-4" data-testid="site-performance-tab">
      <div className="space-y-3">
        <LinkedEntityFilters
          testIdPrefix="siteperf-filter"
          value={entity}
          onChange={setEntity}
          show={filtersShow}
        />
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="label-mute mb-1">Start</div>
            <input data-testid="siteperf-start" type="date" className={inputClass} value={start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} />
          </div>
          <div>
            <div className="label-mute mb-1">End</div>
            <input data-testid="siteperf-end" type="date" className={inputClass} value={end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} />
          </div>
          <div className="ml-auto inline-flex items-center gap-3">
            <div className="text-xs font-mono text-zinc-500 pb-2" data-testid="siteperf-count">
              {loading ? "…" : `${sortedSites.length} site${sortedSites.length === 1 ? "" : "s"}`}
            </div>
            <GhostButton data-testid="siteperf-refresh" onClick={fetchData}>
              <RefreshCw className="size-3.5" /> Refresh
            </GhostButton>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-zinc-500 scanlines border border-[#27272A] bg-[#121212]" data-testid="siteperf-loading">
          Loading site performance…
        </div>
      ) : sortedSites.length === 0 ? (
        <div className="text-center py-16 text-zinc-500 scanlines border border-[#27272A] bg-[#121212]" data-testid="siteperf-empty">
          No sites in scope.
        </div>
      ) : (
        <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-[#27272A]">
              <tr>
                <th
                  className="px-3 py-2 text-left cursor-pointer hover:text-[#FF3B30]"
                  onClick={() => toggleSort("site_name")}
                  data-testid="siteperf-col-site"
                >
                  <div className="label-mute">
                    Site {sortKey === "site_name" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left cursor-pointer hover:text-[#FF3B30]"
                  onClick={() => toggleSort("client_name")}
                >
                  <div className="label-mute">Client {sortKey === "client_name" ? (sortDir === "asc" ? "↑" : "↓") : ""}</div>
                </th>
                {COLS.map((c) => (
                  <th
                    key={c.key}
                    className="px-3 py-2 text-right cursor-pointer hover:text-[#FF3B30]"
                    title={c.help}
                    onClick={() => toggleSort(c.key)}
                    data-testid={`siteperf-col-${c.key}`}
                  >
                    <div className="label-mute whitespace-nowrap">
                      {c.label} {sortKey === c.key ? (sortDir === "asc" ? "↑" : "↓") : ""}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {sortedSites.map((s) => (
                <tr key={s.site_id} className="hover:bg-[#1c1c1e]" data-testid={`siteperf-row-${s.site_id}`}>
                  <td className="px-3 py-2 text-zinc-200">{s.site_name}</td>
                  <td className="px-3 py-2 text-zinc-500">{s.client_name || "—"}</td>
                  {COLS.map((c) => {
                    const v = s[c.key] ?? 0;
                    const cls = toneForValue(c, v, s);
                    return (
                      <td key={c.key} className={`px-3 py-2 text-right font-mono ${cls}`} data-testid={`siteperf-cell-${s.site_id}-${c.key}`}>
                        {c.isPct ? (s.tours_expected ? `${v}%` : "—") : v}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
